/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { I18nMessage } from "@i18n/react";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import { useIsBlockedFromManagingProfiles, useRequestMe } from "@packages/sdk";
import { showModal } from "@packages/ui";
import type { HTMLAttributes, MouseEventHandler } from "react";
import { Fragment, useCallback, useMemo } from "react";

import { useIsKidsProfilesVisible } from "../hooks/useIsKidsProfilesVisible";
import { useKidProfiles } from "../hooks/useKidProfiles";
import { useRequestMeRelatedAccounts } from "../requests/useRequestMeRelatedAccounts";
import type { CreationProps } from "./Creation/Creation";
import { Creation } from "./Creation/Creation";
import type { SwitchProfilesProps } from "./SwitchProfiles";
import { SwitchProfiles } from "./SwitchProfiles";

const cssButton: Interpolation = {
  backgroundColor: "transparent",
  border: "none",
  borderRadius: 0,
  color: "inherit",
  cursor: "pointer",
  padding: 0,
  margin: 0,
};

export const ProfilesSwitch = ({
  children,
  creationProps = {},
  onClick: onClickProps,
  switchProfilesProps = {},
  ...props
}: HTMLAttributes<HTMLButtonElement> & {
  creationProps?: CreationProps;
  switchProfilesProps?: Omit<SwitchProfilesProps, "creationProps">;
}) => {
  const { track } = useAnalytics();
  const {
    query: { data: me },
  } = useRequestMe();
  const { data: meRelatedAccounts } = useRequestMeRelatedAccounts();
  const kidProfiles = useKidProfiles();
  const isBlockedFromManagingProfiles = useIsBlockedFromManagingProfiles();
  const isKidsProfilesVisible = useIsKidsProfilesVisible();

  const isToAddProfile = useMemo(
    () =>
      !me?.is_kid &&
      !isBlockedFromManagingProfiles &&
      kidProfiles?.length === 0,
    [isBlockedFromManagingProfiles, me?.is_kid, kidProfiles],
  );

  const onClick = useCallback<MouseEventHandler<HTMLButtonElement>>(
    (event) => {
      onClickProps?.(event);
      track({
        event: HallowAnalyticsEvent.TappedProfileSelector,
        properties: {
          account_type: me?.is_kid ? "kids" : "standard",
        },
      });
      /** Handle Profile Switch */
      if (isToAddProfile) showModal(Creation, creationProps);
      else showModal(SwitchProfiles, { creationProps, ...switchProfilesProps });
    },
    [
      creationProps,
      isToAddProfile,
      me?.is_kid,
      onClickProps,
      switchProfilesProps,
      track,
    ],
  );

  // fragment and not null because parents are often passed asChild
  if (!isKidsProfilesVisible) return <Fragment />;

  return (
    <button css={cssButton} onClick={onClick} type="button" {...props}>
      {meRelatedAccounts && (
        <Fragment>
          {children}
          <I18nMessage
            message={
              isToAddProfile
                ? "profile_switcher_add_kids_profile"
                : "profile_switcher_title"
            }
          />
        </Fragment>
      )}
    </button>
  );
};
